import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension' // required for devtools typing
import type { Document, DocumentCategory } from '@/lib/schemas'
import { isDocumentInFlight, isDocumentReadyForAnalysis } from '@/lib/schemas'
import {
  api,
  requestUploadUrl,
  uploadFileToS3,
  getDownloadUrl,
  retryDocumentProcessing,
} from '@/lib/api'
import { appendIfAbsentById } from '@/lib/utils'
import { mapWithConcurrency, MAX_PARALLEL_UPLOADS } from '@/lib/map-with-concurrency'
import { bidDocumentCategory } from '@/lib/bid-document-groups'

// ===================
// Types
// ===================

export type UploadStatus = 'pending' | 'uploading' | 'uploaded' | 'error'

export interface UploadingFile {
  id: string
  file: File
  progress: number
  status: UploadStatus
  error?: string
  documentId?: string
  s3Key?: string
  category?: DocumentCategory
}

interface CreateDocumentPayload {
  documentId: string
  name: string
  type: DocumentCategory
  mimeType: string
  size: number
  s3Key: string
  bidId?: string
  metadata?: Record<string, unknown>
}

export type DocumentSortKey = 'name' | 'size' | 'date'
export type SortDirection = 'asc' | 'desc'

interface DocumentsState {
  // State
  documents: Document[]
  uploadingFiles: UploadingFile[]
  isLoading: boolean
  error: string | null
  currentBidId: string | null

  // Selection / sort / filter (UI state)
  selectedDocumentIds: Set<string>
  sortKey: DocumentSortKey
  sortDir: SortDirection
  searchQuery: string
  categoryFilter: Set<DocumentCategory>

  // Actions - Fetching
  fetchDocuments: (bidId: string) => Promise<void>

  // Actions - Uploading
  addFilesToUpload: (files: File[]) => void
  uploadFiles: (bidId: string) => Promise<void>
  /** Resolves with the documents that were confirmed; empty when all failed. */
  uploadTenderDocuments: (bidId: string, files: File[]) => Promise<Document[]>
  updateFileProgress: (fileId: string, progress: number) => void
  updateFileStatus: (fileId: string, status: UploadStatus, error?: string) => void
  updateFileCategory: (fileId: string, category: DocumentCategory) => void
  removeUploadingFile: (fileId: string) => void
  clearUploadingFiles: () => void

  // Actions - Confirming uploads (creating document records)
  confirmUploads: (bidId: string) => Promise<void>

  // Actions - Document operations
  downloadDocument: (documentId: string) => Promise<void>
  deleteDocument: (documentId: string) => Promise<void>
  /**
   * Move one bid document to a category ("Move to" / "Keep here"). Optimistic;
   * rolls the row back and rethrows on failure so the caller can toast.
   */
  setDocumentCategory: (documentId: string, category: DocumentCategory) => Promise<void>
  /**
   * Move several documents at once. The server is all-or-nothing, so on any
   * failure every optimistic change is rolled back and the error rethrown.
   */
  bulkCategorize: (documentIds: string[], category: DocumentCategory) => Promise<void>
  bulkDelete: (documentIds: string[]) => Promise<void>
  setDocumentAddendumRole: (documentId: string, role: 'addendum' | 'none') => Promise<void>
  retryDocument: (documentId: string) => Promise<void>

  // Actions - Selection
  toggleSelection: (documentId: string) => void
  selectAllDocuments: (documentIds: string[]) => void
  clearSelection: () => void

  // Actions - Sort / filter
  setSort: (key: DocumentSortKey) => void
  setSearchQuery: (query: string) => void
  toggleCategoryFilter: (category: DocumentCategory) => void
  clearCategoryFilter: () => void

  // Actions - Polling for processing status
  pollDocuments: () => void
  stopPolling: () => void

  // Actions - Utility
  clearError: () => void

  // Computed
  getUploadedFiles: () => UploadingFile[]
  allFilesUploaded: () => boolean
  allFilesCategorized: () => boolean
  hasProcessingDocuments: () => boolean
  // Document readiness helpers (for bid analysis)
  getProcessedDocuments: () => Document[]
  hasProcessedDocuments: () => boolean
  getProcessedDocumentCount: () => number
  // Filtered + sorted documents for display
  getVisibleDocuments: () => Document[]
}

// Polling configuration
export const POLL_INTERVAL = 3000 // 3 seconds
// The budget must cover the *whole* in-flight window, which since the status
// lifecycle fix is extraction + DOCX→PDF conversion (LibreOffice Lambda: cold
// start + convert + callback) + vector indexing (~1 min observed for indexing
// alone). If it expires early, stopPolling() fires and useDocumentProcessingPoll
// cannot re-arm — its inFlightKey is derived from the same stale store docs — so
// the pill and the "N of M ready" banner freeze on Processing forever and
// Analyze Bid stays gated until a manual refetch.
const BASE_POLL_ATTEMPTS = 60 // 180 seconds base
const POLL_ATTEMPTS_PER_DOC = 20 // +60 seconds per in-flight document (one doc → 80 attempts ≈ 4 min)

// Store polling state outside of Zustand.
//
// `pollingBidId` is the cycle's owner: while it is set, a poll loop is already
// running for that bid and `pollDocuments()` is a no-op. Without it, callers
// that re-invoke `pollDocuments()` (React effects re-running on a fresh
// `documents` array identity) cancelled the pending 3 s timeout and fired an
// immediate, undelayed fetch — a poll storm at HTTP round-trip speed.
//
// `pollCycleId` invalidates in-flight closures: a response that lands after
// `stopPolling()` must not schedule the next tick.
let pollingTimeoutId: ReturnType<typeof setTimeout> | null = null
let pollAttempts = 0
let pollingBidId: string | null = null
let pollCycleId = 0

/**
 * True once the store has moved to a *different* bid than `bidId`.
 *
 * Uploads and fetches resolve after an await, and the user can switch bids
 * without the page being rebuilt (the notifications bell, browser Back/Forward).
 * Merging the result into `documents` regardless puts one bid's file in another
 * bid's list — measured at ~1.3s of bid A's document sitting in bid B's list,
 * long enough to tick and delete it believing it belonged to B.
 *
 * Deliberately false when `currentBidId` is null: that means the store is not
 * scoped to a bid yet, which is absence of information rather than a mismatch.
 */
function hasMovedToAnotherBid(currentBidId: string | null, bidId: string): boolean {
  return currentBidId !== null && currentBidId !== bidId
}

/**
 * A document as it looks once a person has chosen its category: grouped by
 * that category and no longer carrying the "Suggested" label. Mirrors what the
 * next `GET /bids/{id}/documents` returns, so the row doesn't jump on refetch.
 */
function withUserCategory(doc: Document, category: DocumentCategory): Document {
  return {
    ...doc,
    type: category,
    effectiveCategory: category,
    suggestedCategory: null,
    categorySource: 'user',
  }
}

/** Put the given snapshots back in place of their rows, leaving other rows alone. */
function restoreDocuments(documents: Document[], snapshots: Document[]): Document[] {
  const byId = new Map(snapshots.map((d) => [d.id, d]))
  return documents.map((doc) => byId.get(doc.id) ?? doc)
}

export const useDocumentsStore = create<DocumentsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      documents: [],
      uploadingFiles: [],
      isLoading: false,
      error: null,
      currentBidId: null,
      selectedDocumentIds: new Set<string>(),
      sortKey: 'date' as DocumentSortKey,
      sortDir: 'desc' as SortDirection,
      searchQuery: '',
      categoryFilter: new Set<DocumentCategory>(),

      // Fetch documents for a bid
      fetchDocuments: async (bidId: string) => {
        try {
          // Reset selection when switching bids
          const prevBidId = get().currentBidId
          const resetSelection = prevBidId !== bidId
          set({
            isLoading: true,
            error: null,
            currentBidId: bidId,
            // The upload queue is scoped to the bid the files were dropped on.
            // It is not keyed by bid, so without this an in-progress — or worse,
            // a failed — upload row from the previous bid renders in this bid's
            // document list. A failed row has nothing to clear it, so it sits
            // under the wrong bid until reload. Observed in the browser.
            ...(resetSelection
              ? { selectedDocumentIds: new Set<string>(), uploadingFiles: [] }
              : {}),
          })
          const documents = await api.get<Document[]>(`/bids/${bidId}/documents`)
          if (hasMovedToAnotherBid(get().currentBidId, bidId)) return
          set({ documents, isLoading: false })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch documents',
            isLoading: false,
          })
        }
      },

      // Add files to upload queue
      addFilesToUpload: (files: File[]) => {
        const newFiles: UploadingFile[] = files.map((file) => ({
          id: crypto.randomUUID(),
          file,
          progress: 0,
          status: 'pending' as UploadStatus,
        }))
        set((state) => ({
          uploadingFiles: [...state.uploadingFiles, ...newFiles],
        }))
      },

      // Upload all pending files to S3
      uploadFiles: async (bidId: string) => {
        const { uploadingFiles } = get()
        const pendingFiles = uploadingFiles.filter((f) => f.status === 'pending')

        // Upload in parallel, a few at a time (see MAX_PARALLEL_UPLOADS)
        await mapWithConcurrency(
          pendingFiles,
          MAX_PARALLEL_UPLOADS,
          async (uploadingFile) => {
            try {
              // Update status to uploading
              set((state) => ({
                uploadingFiles: state.uploadingFiles.map((f) =>
                  f.id === uploadingFile.id ? { ...f, status: 'uploading' as UploadStatus } : f
                ),
              }))

              // Request presigned URL
              const { uploadUrl, documentId, s3Key } = await requestUploadUrl(
                uploadingFile.file.name,
                uploadingFile.file.type,
                uploadingFile.file.size,
                bidId
              )

              // Upload to S3 with progress tracking
              await uploadFileToS3(uploadUrl, uploadingFile.file, (progress) => {
                set((state) => ({
                  uploadingFiles: state.uploadingFiles.map((f) =>
                    f.id === uploadingFile.id ? { ...f, progress } : f
                  ),
                }))
              })

              // Update status to uploaded
              set((state) => ({
                uploadingFiles: state.uploadingFiles.map((f) =>
                  f.id === uploadingFile.id
                    ? { ...f, status: 'uploaded' as UploadStatus, progress: 100, documentId, s3Key }
                    : f
                ),
              }))
            } catch (error) {
              // Update status to error
              set((state) => ({
                uploadingFiles: state.uploadingFiles.map((f) =>
                  f.id === uploadingFile.id
                    ? {
                        ...f,
                        status: 'error' as UploadStatus,
                        error: error instanceof Error ? error.message : 'Upload failed',
                      }
                    : f
                ),
              }))
            }
          }
        )
      },

      // Upload files as tender documents (auto-categorize and confirm)
      uploadTenderDocuments: async (bidId: string, files: File[]) => {
        // Add files to uploading queue
        const newFiles: UploadingFile[] = files.map((file) => ({
          id: crypto.randomUUID(),
          file,
          progress: 0,
          status: 'pending' as UploadStatus,
          category: 'rfp_tender' as DocumentCategory, // Auto-assign category
        }))

        set((state) => ({
          uploadingFiles: [...state.uploadingFiles, ...newFiles],
          currentBidId: bidId,
        }))

        // Upload in parallel, a few at a time (see MAX_PARALLEL_UPLOADS), and
        // confirm each immediately. Files not yet started stay 'pending'.
        const results = await mapWithConcurrency(
          newFiles,
          MAX_PARALLEL_UPLOADS,
          async (uploadingFile) => {
            try {
              // Update status to uploading
              set((state) => ({
                uploadingFiles: state.uploadingFiles.map((f) =>
                  f.id === uploadingFile.id ? { ...f, status: 'uploading' as UploadStatus } : f
                ),
              }))

              // Request presigned URL
              const { uploadUrl, documentId, s3Key } = await requestUploadUrl(
                uploadingFile.file.name,
                uploadingFile.file.type,
                uploadingFile.file.size,
                bidId
              )

              // Upload to S3 with progress tracking
              await uploadFileToS3(uploadUrl, uploadingFile.file, (progress) => {
                set((state) => ({
                  uploadingFiles: state.uploadingFiles.map((f) =>
                    f.id === uploadingFile.id ? { ...f, progress } : f
                  ),
                }))
              })

              // Immediately create document record (auto-confirm as rfp_tender)
              const payload: CreateDocumentPayload = {
                documentId,
                name: uploadingFile.file.name,
                type: 'rfp_tender',
                mimeType: uploadingFile.file.type,
                size: uploadingFile.file.size,
                s3Key,
                bidId,
              }
              const newDocument = await api.post<Document>('/documents', payload)

              // Add document to list and remove from uploading queue. Merged by
              // id, not appended: a poll tick that landed while this create call
              // was in flight has already put the row in the list.
              //
              // The bid check is the same guard `pollDocuments` applies. An upload
              // started on bid A can resolve after the user has moved to bid B —
              // measured at ~1.3s of bid A's document sitting in bid B's list,
              // long enough to tick and delete it believing it belonged to B.
              // The document itself is created correctly against bid A either
              // way; only the list it is merged into would be wrong.
              //
              // The finished upload still leaves the queue on a mismatch — it has
              // completed, and leaving it would strand a progress row under the
              // wrong bid. Only the merge into `documents` is skipped; bid A picks
              // the row up from `fetchDocuments` when the user returns to it.
              const isSameBid = !hasMovedToAnotherBid(get().currentBidId, bidId)
              set((state) => ({
                documents: isSameBid
                  ? appendIfAbsentById(state.documents, newDocument)
                  : state.documents,
                uploadingFiles: state.uploadingFiles.filter((f) => f.id !== uploadingFile.id),
              }))

              return newDocument
            } catch (error) {
              // Update status to error (keep in uploading list to show error)
              set((state) => ({
                uploadingFiles: state.uploadingFiles.map((f) =>
                  f.id === uploadingFile.id
                    ? {
                        ...f,
                        status: 'error' as UploadStatus,
                        error: error instanceof Error ? error.message : 'Upload failed',
                      }
                    : f
                ),
              }))

              return null
            }
          }
        )

        // Start polling for processing status updates
        get().pollDocuments()

        return results.filter((doc): doc is Document => doc !== null)
      },

      // Update file progress
      updateFileProgress: (fileId: string, progress: number) => {
        set((state) => ({
          uploadingFiles: state.uploadingFiles.map((f) =>
            f.id === fileId ? { ...f, progress } : f
          ),
        }))
      },

      // Update file status
      updateFileStatus: (fileId: string, status: UploadStatus, error?: string) => {
        set((state) => ({
          uploadingFiles: state.uploadingFiles.map((f) =>
            f.id === fileId
              ? error !== undefined
                ? { ...f, status, error }
                : { ...f, status }
              : f
          ),
        }))
      },

      // Update file category
      updateFileCategory: (fileId: string, category: DocumentCategory) => {
        set((state) => ({
          uploadingFiles: state.uploadingFiles.map((f) =>
            f.id === fileId ? { ...f, category } : f
          ),
        }))
      },

      // Remove a file from upload queue
      removeUploadingFile: (fileId: string) => {
        set((state) => ({
          uploadingFiles: state.uploadingFiles.filter((f) => f.id !== fileId),
        }))
      },

      // Clear all uploading files
      clearUploadingFiles: () => {
        set({ uploadingFiles: [] })
      },

      // Confirm uploads by creating document records in the database
      confirmUploads: async (bidId: string) => {
        const { uploadingFiles } = get()
        const uploadedFiles = uploadingFiles.filter(
          (f) => f.status === 'uploaded' && f.category && f.documentId && f.s3Key
        )

        try {
          set({ isLoading: true, error: null })

          // Create document records for each uploaded file
          const newDocuments = await Promise.all(
            uploadedFiles.map(async (file) => {
              const payload: CreateDocumentPayload = {
                documentId: file.documentId!,
                name: file.file.name,
                type: file.category!,
                mimeType: file.file.type,
                size: file.file.size,
                s3Key: file.s3Key!,
                bidId,
              }
              return api.post<Document>('/documents', payload)
            })
          )

          // Add new documents to state and clear uploading files. Merged by id
          // so a concurrent poll response can't leave the same document listed
          // twice (once processing, once pending).
          //
          // Same bid guard as the single-file path above: these records belong to
          // `bidId`, so they are only merged while `bidId` is still the bid on
          // screen. The queue is cleared either way — the uploads finished.
          const isSameBid = !hasMovedToAnotherBid(get().currentBidId, bidId)
          set((state) => ({
            documents: isSameBid
              ? newDocuments.reduce(appendIfAbsentById, state.documents)
              : state.documents,
            uploadingFiles: [],
            isLoading: false,
          }))

          // Start polling for processing status updates. Pointless for a bid the
          // user has left — `pollDocuments` is scoped to `currentBidId` and would
          // poll the wrong bid's documents.
          if (isSameBid) get().pollDocuments()
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to save documents',
            isLoading: false,
          })
          throw error
        }
      },

      // Download a document
      downloadDocument: async (documentId: string) => {
        try {
          const downloadUrl = await getDownloadUrl(documentId)
          // Open in new tab to trigger download
          window.open(downloadUrl, '_blank')
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to get download URL',
          })
          throw error
        }
      },

      // Delete a document
      deleteDocument: async (documentId: string) => {
        try {
          set({ isLoading: true, error: null })
          await api.delete(`/documents/${documentId}`)
          set((state) => ({
            documents: state.documents.filter((d) => d.id !== documentId),
            isLoading: false,
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete document',
            isLoading: false,
          })
          throw error
        }
      },

      // Move one document to a category. The write returns a plain Document
      // (no list enrichment), so the row keeps its enrichment and takes the
      // user-chosen placement: grouped by the new type, no longer "Suggested".
      setDocumentCategory: async (documentId: string, category: DocumentCategory) => {
        const previous = get().documents.find((d) => d.id === documentId)
        if (!previous) return
        set((state) => ({
          documents: state.documents.map((doc) =>
            doc.id === documentId ? withUserCategory(doc, category) : doc
          ),
        }))
        try {
          const updated = await api.patch<Document>(`/documents/${documentId}/category`, {
            type: category,
          })
          set((state) => ({
            documents: state.documents.map((doc) =>
              doc.id === documentId ? withUserCategory({ ...doc, ...updated }, updated.type) : doc
            ),
          }))
        } catch (error) {
          set((state) => ({ documents: restoreDocuments(state.documents, [previous]) }))
          throw error
        }
      },

      // Bulk categorize documents. All-or-nothing on the server (spec 25 §5.2):
      // a refusal writes nothing, so every optimistic change is rolled back.
      bulkCategorize: async (documentIds: string[], category: DocumentCategory) => {
        const ids = new Set(documentIds)
        const previous = get().documents.filter((d) => ids.has(d.id))
        set((state) => ({
          documents: state.documents.map((doc) =>
            ids.has(doc.id) ? withUserCategory(doc, category) : doc
          ),
        }))
        try {
          const updatedDocuments = await api.post<Document[]>('/documents/bulk-categorize', {
            documentIds,
            type: category,
          })
          const byId = new Map(updatedDocuments.map((u) => [u.id, u]))
          set((state) => ({
            documents: state.documents.map((doc) => {
              const updated = byId.get(doc.id)
              return updated ? withUserCategory({ ...doc, ...updated }, updated.type) : doc
            }),
          }))
        } catch (error) {
          set((state) => ({ documents: restoreDocuments(state.documents, previous) }))
          throw error
        }
      },

      // Mark or dismiss a document as an addendum (manual user action). The
      // backend stamps documentRoleSource='user' so detection never overwrites
      // the decision, and for a bid document moves the category with it (mark →
      // Addenda, dismiss → Conditions & Scope). The returned document is merged
      // in place, then the list is quietly refetched so the enrichment — the
      // group the row sits under — comes from the server's policy.
      setDocumentAddendumRole: async (documentId: string, role: 'addendum' | 'none') => {
        const updated = await api.patch<Document>(`/documents/${documentId}/addendum`, { role })
        set((state) => ({
          documents: state.documents.map((doc) => {
            if (doc.id !== documentId) return doc
            if (!updated) return doc
            const merged = { ...doc, ...updated }
            return doc.effectiveCategory === undefined
              ? merged
              : withUserCategory(merged, updated.type)
          }),
        }))
        const bidId = get().currentBidId
        if (!bidId) return
        try {
          const documents = await api.get<Document[]>(`/bids/${bidId}/documents`)
          if (get().currentBidId !== bidId) return
          set({ documents })
        } catch {
          // Non-fatal: the merged row above is already in the right place, and
          // the next fetch or poll brings the enrichment back.
        }
      },

      // Retry a failed document. The server queues the re-run and answers 202;
      // flip the row into flight locally so the status poll (keyed on
      // isDocumentInFlight) picks it up and walks it to Ready/Failed. Errors
      // (not retryable, retry cap, etc.) propagate to the caller to toast.
      retryDocument: async (documentId: string) => {
        await retryDocumentProcessing(documentId)
        set((state) => ({
          documents: state.documents.map((doc) =>
            doc.id === documentId
              ? { ...doc, processingStatus: 'pending', indexingStatus: 'pending' }
              : doc
          ),
        }))
        get().pollDocuments()
      },

      // Bulk delete documents
      bulkDelete: async (documentIds: string[]) => {
        if (documentIds.length === 0) return
        try {
          set({ isLoading: true, error: null })
          // Backend has no dedicated bulk endpoint yet — fan out individual deletes.
          // Settled to surface partial-failure state rather than abort on first error.
          const results = await Promise.allSettled(
            documentIds.map((id) => api.delete(`/documents/${id}`))
          )
          const succeeded = new Set<string>()
          results.forEach((r, idx) => {
            if (r.status === 'fulfilled') succeeded.add(documentIds[idx]!)
          })
          set((state) => {
            const nextSelected = new Set(state.selectedDocumentIds)
            for (const id of succeeded) nextSelected.delete(id)
            return {
              documents: state.documents.filter((d) => !succeeded.has(d.id)),
              selectedDocumentIds: nextSelected,
              isLoading: false,
            }
          })
          const failed = results.filter((r) => r.status === 'rejected')
          if (failed.length > 0) {
            const first = failed[0]
            const message =
              first && first.status === 'rejected' && first.reason instanceof Error
                ? first.reason.message
                : 'Failed to delete some documents'
            set({ error: message })
            throw new Error(message)
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete documents',
            isLoading: false,
          })
          throw error
        }
      },

      // Selection actions
      toggleSelection: (documentId: string) => {
        set((state) => {
          const next = new Set(state.selectedDocumentIds)
          if (next.has(documentId)) {
            next.delete(documentId)
          } else {
            next.add(documentId)
          }
          return { selectedDocumentIds: next }
        })
      },

      selectAllDocuments: (documentIds: string[]) => {
        set({ selectedDocumentIds: new Set(documentIds) })
      },

      clearSelection: () => {
        set({ selectedDocumentIds: new Set<string>() })
      },

      // Sort / filter actions
      setSort: (key: DocumentSortKey) => {
        set((state) => {
          if (state.sortKey === key) {
            return { sortDir: state.sortDir === 'asc' ? 'desc' : 'asc' }
          }
          return { sortKey: key, sortDir: key === 'name' ? 'asc' : 'desc' }
        })
      },

      setSearchQuery: (query: string) => {
        set({ searchQuery: query })
      },

      toggleCategoryFilter: (category: DocumentCategory) => {
        set((state) => {
          const next = new Set(state.categoryFilter)
          if (next.has(category)) {
            next.delete(category)
          } else {
            next.add(category)
          }
          return { categoryFilter: next }
        })
      },

      clearCategoryFilter: () => {
        set({ categoryFilter: new Set<DocumentCategory>() })
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Get only uploaded files (for categorization modal)
      getUploadedFiles: () => {
        return get().uploadingFiles.filter((f) => f.status === 'uploaded')
      },

      // Check if all files have been uploaded
      allFilesUploaded: () => {
        const { uploadingFiles } = get()
        return (
          uploadingFiles.length > 0 &&
          uploadingFiles.every((f) => f.status === 'uploaded' || f.status === 'error')
        )
      },

      // Check if all uploaded files have categories assigned
      allFilesCategorized: () => {
        const uploadedFiles = get().getUploadedFiles()
        return uploadedFiles.length > 0 && uploadedFiles.every((f) => f.category)
      },

      // Check if any documents are still processing or indexing
      hasProcessingDocuments: () => {
        return get().documents.some(isDocumentInFlight)
      },

      // Documents available to RAG — the one definition of "ready" (F10).
      // Shared with the row badge via `isDocumentReadyForAnalysis` so the
      // banner count, the Analyze gate and the per-row status never disagree.
      getProcessedDocuments: () => {
        return get().documents.filter(isDocumentReadyForAnalysis)
      },

      // Check if at least one document is ready for analysis
      hasProcessedDocuments: () => {
        return get().getProcessedDocuments().length > 0
      },

      // Get count of processed documents (for progress display)
      getProcessedDocumentCount: () => {
        return get().getProcessedDocuments().length
      },

      // Filtered + sorted documents for display in the bid documents tab
      getVisibleDocuments: () => {
        const { documents, searchQuery, categoryFilter, sortKey, sortDir } = get()
        const q = searchQuery.trim().toLowerCase()
        const filtered = documents.filter((d) => {
          if (categoryFilter.size > 0 && !categoryFilter.has(bidDocumentCategory(d))) return false
          if (q && !d.name.toLowerCase().includes(q)) return false
          return true
        })
        const dir = sortDir === 'asc' ? 1 : -1
        return [...filtered].sort((a, b) => {
          if (sortKey === 'name') return a.name.localeCompare(b.name) * dir
          if (sortKey === 'size') return (a.size - b.size) * dir
          // date — DocumentSchema only guarantees createdAt
          const aTime = new Date(a.createdAt).getTime()
          const bTime = new Date(b.createdAt).getTime()
          return (aTime - bTime) * dir
        })
      },

      // Start polling for document processing status updates
      pollDocuments: () => {
        const { currentBidId, documents, hasProcessingDocuments } = get()

        // Don't poll if no bid is selected or no processing documents
        if (!currentBidId || !hasProcessingDocuments()) {
          return
        }

        // A cycle already owns the loop for this bid — asking again is a no-op,
        // not a restart. This is the safety catch for the request storm (F6).
        if (pollingBidId === currentBidId) {
          return
        }

        // A cycle for a *different* bid is still running — retire it first.
        get().stopPolling()

        pollingBidId = currentBidId
        const cycleId = ++pollCycleId

        // Scale max attempts by number of processing documents (Lambda concurrency
        // = 10). Recomputed on every tick from the live list and never lowered:
        // documents uploaded mid-cycle must extend the budget, and documents
        // finishing must not shrink it out from under the ones still running.
        let maxPollAttempts = 0
        const raiseBudget = (docs: Document[]) => {
          // Counted with isDocumentInFlight — the same predicate that decides
          // whether the poll keeps going. The budget has to cover the whole
          // in-flight window (extraction + DOCX conversion + indexing), because
          // the poll now lives until indexing is terminal. Counting extraction
          // only left the base 30 attempts (~90 s) to cover a conversion that
          // routinely outlives it: the poll stopped mid-flight, the banner spun
          // forever, and useDocumentProcessingPoll could not re-arm because its
          // inFlightKey never changed.
          const processingCount = docs.filter(isDocumentInFlight).length
          maxPollAttempts = Math.max(
            maxPollAttempts,
            BASE_POLL_ATTEMPTS + processingCount * POLL_ATTEMPTS_PER_DOC
          )
        }
        raiseBudget(documents)

        const poll = async () => {
          // A newer cycle (or stopPolling) has superseded this closure.
          if (cycleId !== pollCycleId) return

          const { currentBidId: bidId, hasProcessingDocuments: checkProcessing } = get()

          // Stop if bid changed or no more processing documents
          if (!bidId || !checkProcessing()) {
            get().stopPolling()
            return
          }

          raiseBudget(get().documents)

          // Stop if max attempts reached
          if (pollAttempts >= maxPollAttempts) {
            console.warn(`[documents-store] Max poll attempts (${maxPollAttempts}) reached, stopping polling`)
            get().stopPolling()
            return
          }

          try {
            pollAttempts++
            const documents = await api.get<Document[]>(`/bids/${bidId}/documents`)
            if (cycleId !== pollCycleId) return
            set({ documents })

            // Check if we should continue polling (processing or indexing)
            const stillProcessing = documents.some(isDocumentInFlight)

            if (stillProcessing) {
              pollingTimeoutId = setTimeout(() => void poll(), POLL_INTERVAL)
            } else {
              get().stopPolling()
            }
          } catch (error) {
            if (cycleId !== pollCycleId) return
            console.error('[documents-store] Poll error:', error)
            // Continue polling on error (transient failures)
            pollingTimeoutId = setTimeout(() => void poll(), POLL_INTERVAL)
          }
        }

        // Start polling
        void poll()
      },

      // Stop polling for document status
      stopPolling: () => {
        if (pollingTimeoutId) {
          clearTimeout(pollingTimeoutId)
          pollingTimeoutId = null
        }
        pollAttempts = 0
        pollingBidId = null
        // Invalidate any in-flight poll so a late response can't reschedule.
        pollCycleId++
      },
    }),
    { name: 'documents-store' }
  )
)
